import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { environment } from 'src/environments/environment';
import { AddCartItemRequest, CartItemsRequest, DeleteCartItemRequest, UpdateCartItemQuantityRequest } from '../entities/cart-item';

export interface B2BUserFilterRequest {
  shipToBp: string;
  pageNumber: number;
  rowsPerPage: number;
}

@Injectable({
  providedIn: 'root'
})
export class B2bUserCartService {

  private readonly b2bApiRoot = environment.b2bApiRoot;

  private readonly urlCartItems = `${this.b2bApiRoot}/api/impact/cart/items`;
  private readonly urlAddCartItem = `${this.b2bApiRoot}/api/impact/cart/item`;
  private readonly urlDeleteCartItem = `${this.b2bApiRoot}/api/impact/cart/item`;
  private readonly urlUpdateCartItem = `${this.b2bApiRoot}/api/impact/cart/items`;
  private readonly urlDeleteCart = `${this.b2bApiRoot}/api/impact/cart/items`;
  private readonly urlImportCart = '/api/cart/import';
  private readonly urlB2BUsers = `${this.b2bApiRoot}/api/impact/user/filter`;
  private readonly urlB2BRoles = `${this.b2bApiRoot}/api/impact/defaultValues/roles`;

  constructor(
    private apiService: ApiService
  ) { }

  /**
   * Fetches shopping cart items for a B2B user
   * @param payload the cart items request payload containing UserId and ShipToBp
   */
  getCartItems(payload: CartItemsRequest): Observable<any> {
    return this.apiService.postGeneric(this.urlCartItems, payload, true);
  }

  /**
   * Adds an item to the B2B user's shopping cart
   * @param payload the add cart item request payload containing Product, UserId, and ShipToBp
   */
  addCartItem(payload: AddCartItemRequest): Observable<any> {
    return this.apiService.postGeneric(this.urlAddCartItem, payload, true);
  }

  /**
   * Deletes an item from the B2B user's shopping cart
   * @param payload the delete cart item request payload containing ShoppingCartItemId and UserId
   */
  deleteCartItem(payload: DeleteCartItemRequest): Observable<any> {
    const url = `${this.urlDeleteCartItem}/${payload.UserId}/${payload.ShoppingCartItemId}`;
    return this.apiService.deleteGeneric(url, true);
  }

  /**
   * Updates the quantity of an item in the B2B user's shopping cart
   * @param payload the update cart item request payload containing ShoppingCartItemId, Quantity, and UserId
   */
  updateCartItemQuantity(payload: UpdateCartItemQuantityRequest): Observable<any> {
    return this.apiService.putGeneric(this.urlUpdateCartItem, payload, true);
  }

  /**
   * Deletes all items from the B2B user's shopping cart
   * @param userId the user ID whose cart should be deleted
   */
  deleteCart(userId: string): Observable<any> {
    const url = `${this.urlDeleteCart}/${userId}`;
    return this.apiService.deleteGeneric(url, true);
  }

  /**
   * Imports a B2B user's shopping cart to generate a quote
   * @param payload the import cart request payload containing UserId, ShipToBp, and CustomerId
   */
  generateQuote(payload): Observable<any> {
    return this.apiService.post(this.urlImportCart, payload);
  }

  /**
   * Calls the user filter API with the provided payload
   * @param payload the filter request payload
   */
  getB2BUsers(payload: B2BUserFilterRequest): Observable<any> {
    return this.apiService.postGeneric(this.urlB2BUsers, payload, true);
  }

  /**
   * Fetches all available B2B roles with descriptions
   */
  getRoles(): Observable<any> {
    return this.apiService.getGeneric(this.urlB2BRoles, true);
  }
}
